import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "classnames";

import ExcelIcon from "mdi-material-ui/FileExcel";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";

// jaza-soft
import { WithReduxForm, SelectInput, TextInput, Button, showNotification, FileInput, FileField } from "jazasoft";

// local
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";

import useMutation from "../../../hooks/useMutation";

import { useDeepMemo, useSafeState } from "../../../hooks";
import { getDistinctValues, isEmpty, isTrue, listFromCsv } from "../../../utils/helpers";
import { InventoryStatus } from "../../../utils/types";

const sampleTypes = "Size Set Sample, Proto Sample";

const titleMap = {
  sample_issue: "Sample Issue",
  outsource: "Outsource",
  return_supplier: "Return Supplier",
  write_off: "Write Off"
};

const useStyles = makeStyles(theme => ({
  root: {
    width: 680,
    display: "flex",
    flexDirection: "column",
    height: "100vh"
  },
  drawerPaper: {
    maxWidth: 1000
  },
  drawerHeader: {
    display: "flex",
    width: 680,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  footer: {
    display: "flex",
    padding: "1.5em"
  },
  padding: {
    padding: "1.5em"
  },
  marginVertical: {
    marginTop: "1.5em"
  }
}));

const FabricIssueDrawer = props => {
  const classes = useStyles();
  const { open, action, fabric, selectedColor, rollInfoList, onDrawerClose, onRefresh } = props;

  const [selectedIds, setSelectedIds] = useSafeState([]);

  const formValues = useSelector(state => state.form["record-form"]?.values);

  const [mutate] = useMutation();
  const dispatch = useDispatch();
  const [download] = useMutation({ rawResponse: true });

  const choicesSampleTypes = listFromCsv(sampleTypes).map(e => ({ id: e, name: e }));

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "rollNo", title: "Factory Roll No" },
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "length", title: "Length" },
      { dataKey: "invStatus", title: "Inv. Status", filterOptions: { filter: true } },
      { dataKey: "qcStatus", title: "QC Status", filterOptions: { filter: true } },
      {
        dataKey: "issueLength",
        title: "Issue \nLength",
        inputType: "text-input",
        options: { type: "number" }
      },
      { dataKey: "balLength", title: "Balance \nLength", render: (value, _, row) => (row.length || 0) - (row.issueLength || 0) }
    ];

    if (action !== "sample_issue") {
      cols = cols.filter(c => !["issueLength", "balLength"].includes(c.dataKey));
    } else {
      cols = cols.filter(({ filterOptions, ...column }) => column);
    }

    return cols;
  }, [rollInfoList, action]);

  const rows = useDeepMemo(() => {
    if (isEmpty(rollInfoList)) return [];
    let rollList = [];
    if (action === "return_supplier" || action === "write_off") {
      rollList = rollInfoList.filter(ri => ri.invStatus === InventoryStatus.UNALLOCATED);
    } else {
      rollList = rollInfoList
        .filter(
          ri => isEmpty(ri.invStatus) || [InventoryStatus.UNALLOCATED, InventoryStatus.ALLOCATED, InventoryStatus.PLANNED].includes(ri.invStatus)
        )
        .filter(ri => ri.plannedInLay + ri.usedInLay + ri.usedInPanel === 0.0);
    }
    rollList = rollList.filter(ri => isTrue(ri.active)).map(ri => ({ ...ri, issueLength: 0 }));
    return rollList;
  }, [rollInfoList, action]);

  const onSubmit = (rollInfoList, onSubmitComplete) => {
    if (!isEmpty(formValues.file?.rawFile)) {
      const data = new FormData();
      data.append("file", formValues.file?.rawFile);
      const options = { url: `import/excel`, method: "post", data };
      mutate(options, {
        onSuccess: excelSheetList => {
          if (Array.isArray(excelSheetList) && excelSheetList.length > 0) {
            const excelRowList = excelSheetList[0].excelData?.rowList || [];
            if (excelRowList.length > 0) {
              const cellList = excelRowList[0]?.cellList;
              const colNo = cellList.find(c => c.value === "FACTORY ROLL NO")?.colNo;
              if (!isEmpty(colNo)) {
                const dataRows = excelRowList.filter(r => r.rowNo !== 1);
                const rollNos = getDistinctValues(dataRows.map(r => r.cellList.find(c => c.colNo === colNo)?.value))?.filter(e => !isEmpty(e));

                //** Validations **//
                const availableRollNos = getDistinctValues(rows.map(ri => ri.factoryRollNo));
                const invalidRollNos = rollNos.filter(e => !availableRollNos.includes(e));
                if (!isEmpty(invalidRollNos)) {
                  dispatch(showNotification(`Rolls - [${invalidRollNos.join(", ")}] not present in the available Roll List`, { type: "danger" }));
                  return;
                }

                const rollList = rows
                  .filter(ri => rollNos.includes(ri.factoryRollNo))
                  .map(ri => ({
                    id: ri.id,
                    factoryRollNo: ri.rollNo,
                    supplierRollNo: ri.supplierRollNo,
                    length: ri.length
                  }))
                  .filter(ri => ri.length > 0);
                if (isEmpty(rollList)) {
                  const message = `Rolls not selected`;
                  dispatch(showNotification(message, { type: "danger" }));
                  return;
                }

                const options = {
                  url: `material-out`,
                  method: "post",
                  params: { type: action },
                  data: {
                    ...formValues,
                    itemCode: fabric?.itemCode,
                    itemColor: selectedColor?.color,
                    itemColorCode: selectedColor?.colorCode,
                    rollList
                  }
                };

                mutate(options, {
                  onSuccess: () => {
                    dispatch(showNotification(`Material Out (${titleMap[action]}) done successfully.`));
                    onRefresh && onRefresh();
                    onDrawerClose && onDrawerClose();
                  }
                });
              }
            } else {
              dispatch(showNotification("No Data Available in uploaded excel.", { type: "danger" }));
              return;
            }
          } else {
            onSubmitComplete && onSubmitComplete();
          }
        }
      });
    } else {
      const rollList = rollInfoList
        .filter(
          ri => isEmpty(ri.invStatus) || [InventoryStatus.UNALLOCATED, InventoryStatus.ALLOCATED, InventoryStatus.PLANNED].includes(ri.invStatus)
        )
        .map(ri => ({
          id: ri.id,
          factoryRollNo: ri.rollNo,
          supplierRollNo: ri.supplierRollNo,
          length: action === "sample_issue" ? ri.issueLength : ri.length
        }))
        .filter(ri => ri.length > 0);
      if (isEmpty(rollList)) {
        const message = action === "sample_issue" ? `Issue Length not specified in any roll.` : `Rolls not selected`;
        dispatch(showNotification(message, { type: "danger" }));
        return;
      }
      const options = {
        url: `material-out`,
        method: "post",
        params: { type: action },
        data: {
          ...formValues,
          itemCode: fabric?.itemCode,
          itemColor: selectedColor?.color,
          itemColorCode: selectedColor?.colorCode,
          rollList
        }
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Material Out (${titleMap[action]}) done successfully.`));
          onRefresh && onRefresh();
          onDrawerClose && onDrawerClose();
        }
      });
    }
  };

  const onSelect = selectedIds => {
    setSelectedIds(selectedIds);
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  const footerActions = useDeepMemo(() => {
    if (isEmpty(rows)) return [];
    return [
      {
        name: "download",
        tooltip: "Download",
        icon: <ExcelIcon />,
        options: { variant: "outlined", style: { color: "green", borderColor: "green" } }
      }
    ];
  }, [rows]);

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "download") {
      onDownload(onActionComplete, rows);
    }
  };

  const onDownload = (onActionComplete, rows) => {
    const filename = "ROLLS";
    const columnList = columns.map((c, idx) => ({ ...c, colNo: idx + 1, title: c.title.toUpperCase() }));
    const rowList = rows.map((row, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] }))
    }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      params: { filename },
      data: [{ sheetNo: 0, sheetName: "ROLLS", excelData: { columnList, rowList } }]
    };

    download(options, {
      onSuccess: response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

          let blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
        }
        onActionComplete && onActionComplete(rows);
      }
    });
  };

  const diabled = action === "sample_issue" ? isEmpty(formValues?.sampleType) : isEmpty(formValues?.file) && isEmpty(selectedIds);

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" classes={{ paper: classes.drawerPaper }} open={open} onClose={onDrawerClose}>
        <WithReduxForm>
          {() => (
            <div className={classes.root}>
              <div className={classes.drawerHeader}>
                <IconButton onClick={onDrawerClose}>
                  <ChevronRightIcon />
                </IconButton>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%", paddingRight: "1.5em" }}>
                  <Typography variant="h5">{titleMap[action]}</Typography>
                </div>
              </div>
              <Divider />

              <div className={clsx(classes.content, { [classes.padding]: true })}>
                <Grid container spacing={2}>
                  {action === "sample_issue" && (
                    <Grid item xs={6}>
                      <SelectInput source="sampleType" label="Sample Type" choices={choicesSampleTypes} fullWidth />
                    </Grid>
                  )}
                  {action !== "sample_issue" && (
                    <Grid item xs={4}>
                      <TextInput source="challanNo" label="Challan No" fullWidth />
                    </Grid>
                  )}
                  {action !== "sample_issue" && (
                    <Grid item xs={4}>
                      <TextInput source="outDestination" label="Material Out Destination" fullWidth />
                    </Grid>
                  )}
                  <Grid item xs={action === "sample_issue" ? 6 : 4}>
                    <TextInput source="remarks" label="Remarks" fullWidth />
                  </Grid>
                </Grid>

                {action !== "sample_issue" && (
                  <Grid style={{ marginTop: 12 }} container>
                    <FileInput
                      source="file"
                      label="File"
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      xs={12}
                      fullWidth>
                      <FileField source="src" title="title" />
                    </FileInput>
                  </Grid>
                )}

                <div className={clsx({ [classes.marginVertical]: false })}>
                  <MuiTable
                    title={<CardHeader title={"Rolls"} style={{ paddingLeft: 0 }} />}
                    columns={columns}
                    rows={rows}
                    selectable={action !== "sample_issue"}
                    pageable
                    // searchable={!isEmpty(ledger)}
                    searchKeys={["rollNo"]}
                    toolbar
                    footerActions={action !== "sample_issue" ? footerActions : []}
                    onFooterActionClick={onFooterActionClick}
                    onSelect={onSelect}
                    editable={action === "sample_issue"}
                    editing={action === "sample_issue"}
                    onSubmit={(rows, _, onSubmitComplete) => onSubmit(rows, onSubmitComplete)}
                    handleSubmitRef={handleSubmitRef}
                    i18nKey="abc"
                  />
                </div>
              </div>

              <div className={classes.footer}>
                {action === "sample_issue" && (
                  <Button label="Submit" variant="contained" color="primary" onClick={event => ffHandleSubmit(event)}>
                    <ActionSend />
                  </Button>
                )}
                {action !== "sample_issue" && (
                  <Button
                    label="Submit"
                    variant="contained"
                    color="primary"
                    disabled={diabled}
                    onClick={() => onSubmit(rollInfoList.filter(ri => selectedIds.includes(ri.id)))}>
                    <ActionSend />
                  </Button>
                )}
              </div>
            </div>
          )}
        </WithReduxForm>
      </Drawer>
    </div>
  );
};

export default FabricIssueDrawer;
